import React, { useState, useCallback, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  CardContent,
  Typography,
  Divider,
  TableSortLabel,
  TextField,
  useTheme,
  Grid,
  Button,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import ModalGeneric from "../Generic/ModalGeneric";
import Icons from "../../Utils/Icons";
import { GET, POST, DELETE } from "../Generic/CrudRequest";
import Alerts from "../../Utils/Alerts";
import { searchPermissions } from "./PermissionsUser/PermissionsActions";
import useExcelExport from "../../Utils/useExcelExport";
import { SaveAlt } from "@mui/icons-material";

export default function Users(props) {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState("");
  const [column, setColumn] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [bodyModal, setBodyModal] = useState(null);
  const [caseModal, setCaseModal] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [register, setRegister] = useState(false);
  const [edit, setEdit] = useState(false);
  const [changePasswords, setChangePasswords] = useState(false);
  const [canDeleteUser, setCanDeleteUser] = useState(false);
  const [permissionsUser, setPermissionUser] = useState(false);
  // Estados para validación de contraseña
  const [passwordErrors, setPasswordErrors] = useState({});
  const theme = useTheme();
  const { config } = props;
  const exportToExcel = useExcelExport();

  const validatePassword = (password) => {
    const pattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&.,])[A-Za-z\d@$!%*?&.,]{8,}$/;
    return pattern.test(password);
  };

  const columns = [
    { id: "nombre", label: t("users.name"), minWidth: "100px" },
    { id: "email", label: t("users.email"), minWidth: "100px" },
    { id: "userName", label: t("users.userName"), minWidth: "100px" },
  ];

  const handleExport = () => {
    exportToExcel(rows, columns, t("users.excelTitle"));
  };

  const fetchUsers = useCallback(async () => {
    const dataUsers = await GET(`${config.url}/api/User/paginado`, {
      start: page * rowsPerPage,
      length: rowsPerPage,
      busqueda: searchText,
      column: column,
      dir: order,
    });
    // console.log(dataUsers)
    setRows(dataUsers.resultado);
    setTotalRecords(dataUsers.totalRegistros);

    const permissionsArray = JSON.parse(localStorage.getItem("permissions"));

    const permissionRegister = searchPermissions(
      "Usuarios",
      "Registrar",
      permissionsArray
    );

    const permissionEdit = searchPermissions(
      "Usuarios",
      "Editar",
      permissionsArray
    );

    const permissionDelete = searchPermissions(
      "Usuarios",
      "Eliminar",
      permissionsArray
    );

    const permissionChangePassword = searchPermissions(
      "Usuarios",
      "CambiarContraseña",
      permissionsArray
    );

    const permissionsUser = searchPermissions(
      "Usuarios",
      "PermisosUsuario",
      permissionsArray
    );

    setRegister(permissionRegister);
    setEdit(permissionEdit);
    setCanDeleteUser(permissionDelete);
    setChangePasswords(permissionChangePassword);
    setPermissionUser(permissionsUser);
  }, [page, rowsPerPage, column, order, searchText, config.url]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const deleteUser = async (id) => {
    await DELETE(`${config.url}/api/User/${id}`);
    fetchUsers();
  };

  const changePassword = async (id) => {
    if (!newPassword || !repeatPassword) {
      handleShowAlert(t("users.requiredField"), "error");
      return;
    }

    if (!validatePassword(newPassword)) {
      handleShowAlert(t("users.invalidPassword"), "error");
      return;
    }

    if (newPassword !== repeatPassword) {
      handleShowAlert(t("users.passwordsDifferent"), "error");
      return;
    }

    await POST(`${config.url}/api/User/actualizar-password`, {
      idUsuario: id,
      newPass: newPassword,
    });
    
    setNewPassword("");
    setRepeatPassword("");
    setPasswordErrors({});
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = orderBy === property && order === "asc";
    setColumn(columnNumber);
    setOrderBy(property);
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleSearch = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleClear = () => {
    setSearchText("");
  };

  const handleDeleteUser = (userName, id) => {
    setIsModalOpen(true);
    setModalTitle(t("users.deleteUser"));
    setModalDescription(t("users.deleteUserConfirm", { userName }));
    setBodyModal(<></>);
    setUserIdToDelete(id);
    setCaseModal(1);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewPassword("");
    setRepeatPassword("");
    setPasswordErrors({});
  };

  const handleAcceptModal = () => {
    if (caseModal === 1) {
      deleteUser(userIdToDelete);
    } else if (caseModal === 2) {
      changePassword(userIdToDelete);
      setNewPassword("");
      setRepeatPassword("");
      setPasswordErrors({});
    }
    setIsModalOpen(false);
  };

  const handleChangePassword = (id) => {
    setIsModalOpen(true);
    setModalTitle(t("users.changePassword"));
    setModalDescription(t("users.changePasswordDescription"));
    setBodyModal(
      <>
        <Divider sx={{ maxWidth: "100%", margin: "auto", marginTop: 2 }} />
        <Box component="form" autoComplete="off">
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <TextField
                variant="standard"
                label={t("users.newPassword")}
                type="password"
                autoComplete="new-password"
                name="change-password-new"
                id="change-password-new"
                onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
              />
            </Grid>
            <Grid item>
              <TextField
                variant="standard"
                label={t("users.repeatNewPassword")}
                type="password"
                autoComplete="new-password"
                name="change-password-repeat"
                id="change-password-repeat"
                onChange={(e) => handlePasswordChange("repeatPassword", e.target.value)}
                error={!!passwordErrors.repeatPassword}
                helperText={passwordErrors.repeatPassword}
              />
            </Grid>
          </Grid>
        </Box>
      </>
    );
    setUserIdToDelete(id);
    setCaseModal(2);
  };

  const handleShowAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  // Función para validar contraseña en tiempo real
  const handlePasswordChange = (field, value) => {
    if (field === "newPassword") {
      setNewPassword(value);
    } else if (field === "repeatPassword") {
      setRepeatPassword(value);
    }

    // Validar solo si el campo no está vacío
    if (value.trim() !== "") {
      if (!validatePassword(value)) {
        setPasswordErrors({
          ...passwordErrors,
          [field]: t("users.invalidPassword"),
        });
      } else {
        setPasswordErrors({
          ...passwordErrors,
          [field]: "",
        });
      }
    } else {
      // Limpiar error si el campo está vacío
      setPasswordErrors({
        ...passwordErrors,
        [field]: "",
      });
    }
  };

  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        {isModalOpen && (
          <ModalGeneric
            open={isModalOpen}
            title={modalTitle}
            description={modalDescription}
            onClose={handleCloseModal}
            onAccept={handleAcceptModal}
            bodyComponent={bodyModal}
          />
        )}
        <CardContent>
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
              overflow: "hidden",
              maxWidth: "100%",
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{ flex: "1", fontWeight: "bold" }}
            >
              {t("users.title")}
            </Typography>
            {register ? (
              <Icons
                iconName="Add"
                fontSize="large"
                to="/Usuarios/Agregar"
                style={{
                  marginLeft: "auto",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.white.main,
                }}
                tooltip={t("users.addUser")}
              />
            ) : null}
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <CardContent
            sx={{
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              alignItems: "auto",
            }}
          >
            <TextField
              label={t("users.search")}
              InputLabelProps={{
                shrink: true,
              }}
              type="search"
              variant="standard"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 400,
                maxWidth: "100%",
                color: theme.palette.primary.main,
              }}
            />
            <Icons
              iconName="Search"
              fontSize="large"
              onClick={handleSearch}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("users.search")}
            />
            <Icons
              iconName="Clear"
              fontSize="large"
              onClick={handleClear}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("users.clear")}
            />
          </CardContent>
          <Paper
            sx={{
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              overflow: "hidden",
              boxShadow: "none",
              border: "none",
            }}
          >
            <TableContainer>
              <Button
                variant="contained"
                startIcon={<SaveAlt />}
                onClick={handleExport}
              >
                {t("users.excel")}
              </Button>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align="left"
                        style={{
                          minWidth: column.minWidth,
                          fontWeight: "bold",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() =>
                            handleRequestSort(
                              column.id,
                              columns.indexOf(column)
                            )
                          }
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center">
                        {t("users.noUsersAvailable")}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        {columns.map((column) => (
                          <TableCell key={column.id} align="left">
                            {row[column.id]}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          {permissionsUser && (
                            <Icons
                              iconName="GppGood"
                              fontSize="medium"
                              style={{
                                backgroundColor: theme.palette.info.main,
                                color: "#fff",
                              }}
                              to={`/Usuarios/PermisosUsuarios/${row.usuarioId}`}
                              tooltip={t("users.permissions")}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {edit && (
                            <Icons
                              iconName="Edit"
                              fontSize="medium"
                              style={{
                                backgroundColor: theme.palette.primary.main,
                                color: "#fff",
                                marginLeft: 20,
                              }}
                              to={`/Usuarios/Editar/${row.usuarioId}`}
                              tooltip={t("users.editUser")}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {changePasswords && (
                            <Icons
                              onClick={() =>
                                handleChangePassword(row.usuarioId)
                              }
                              iconName="Password"
                              fontSize="medium"
                              style={{
                                backgroundColor: theme.palette.secondary.main,
                                color: "#fff",
                                marginLeft: 20,
                              }}
                              tooltip={t("users.changePasswordUser")}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {canDeleteUser && (
                            <Icons
                              onClick={() =>
                                handleDeleteUser(
                                  row.user.userName,
                                  row.idUsuarioDetalle
                                )
                              }
                              iconName="Delete"
                              fontSize="medium"
                              style={{
                                backgroundColor: theme.palette.danger.main,
                                color: "#fff",
                                marginLeft: 20,
                              }}
                              tooltip={t("users.deleteUserAction")}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100, { value: -1, label: t("pagination.all") }]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t("pagination.labelRowsPerPage")}
              labelDisplayedRows={({ from, to, count }) =>
                t("pagination.displayedRows", { from, to, count })
              }
            />
          </Paper>
        </CardContent>
      </Card>
    </>
  );
}
